import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCheckoutState } from '../../utils/checkoutState';
import { config } from '../../utils/config';
import { pushTrackingEvent } from '../../utils/tracking';
import useScript from '../../utils/useScript';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    Klarna: any;
    klarnaAsyncCallback?: () => void;
  }
}

const KlarnaPayment = () => {
  const scriptLoadingPromise = useScript('https://x.klarnacdn.net/kp/lib/v1/api.js');
  const { checkoutState } = useCheckoutState();
  const { values } = useFormikContext<{ paymentMethod: string }>();
  const selectedPaymentMethod = checkoutState.orderState.payment?.selected?.[0];
  const initializationData = selectedPaymentMethod?.configuration?.initializationData;
  const previousInitializationData = useRef();

  useEffect(() => {
    if (
      !initializationData ||
      JSON.stringify(initializationData) === JSON.stringify(previousInitializationData.current) ||
      selectedPaymentMethod?.paymentOptionKey !== values.paymentMethod
    ) {
      return;
    }

    previousInitializationData.current = initializationData;

    (async () => {
      await scriptLoadingPromise.current;
      window.Klarna.Payments.init({
        client_token: initializationData.clientToken,
      });
      window.Klarna.Payments.load(
        {
          container: '#klarna-payment-container',
          payment_method_category: initializationData.paymentCategory,
        },
        (res: any) => console.debug(res)
      );
    })();
  }, [initializationData]);

  useEffect(
    () => () => {
      if (IS_WEB_COMPONENT) {
        const klarnaPaymentContainer = document.getElementById('klarna-payment-container');
        if (klarnaPaymentContainer) {
          klarnaPaymentContainer.innerHTML = '';
        }
      }
    },
    []
  );

  return (
    <div className="option__form">
      {IS_WEB_COMPONENT ? <slot name="klarna-payment-container" /> : <div id="klarna-payment-container" />}
    </div>
  );
};

export const useKlarnaPayment = () => {
  const { checkoutState, updatePaymentMethod } = useCheckoutState();
  const navigate = useNavigate();

  const authorizeKlarnaPayment = async () => {
    const { paymentOptionKey, configuration } = checkoutState.orderState.payment!.selected![0];
    await updatePaymentMethod({
      paymentOptionKey,
      specification: {
        initialization: {
          sendCustomerData: true,
        },
      },
    });
    // We push the same route onto the history stack again so if users see a modal displayed from klarna and
    // use the browser back button to close it (which is not possible) they don't immediately drop out of the session
    navigate('/payment');
    const authorizationId = await new Promise<string>((resolve, reject) =>
      window.Klarna.Payments.authorize(
        { payment_method_category: configuration?.initializationData?.paymentCategory },
        {},
        (res: { authorization_token?: string; approved: boolean; show_form: boolean; error?: any }) => {
          console.debug(res);
          if (res.approved && res.authorization_token) {
            resolve(res.authorization_token);
          } else {
            reject(res.error);
            pushTrackingEvent({
              event: 'error',
              action: 'klarna',
              label: `${config.country}|${res.error.name}|${res.error.message}`,
              value: null,
            });
          }
        }
      )
    );
    await updatePaymentMethod({
      paymentOptionKey,
      specification: { authorizationId },
    });
  };

  return { authorizeKlarnaPayment };
};

export default KlarnaPayment;
